import { useEffect } from "react";
import { Container } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { LoaderFunctionArgs, Outlet, useLocation, useNavigate } from "react-router-dom";

import { RoutesMap } from "../routes";
import { NetworkError } from "../api";
import { usePersistStore } from "../store/persistStore";
import { useLoginFormStore } from "../store/loginFormStore";
import { AppModalProvider } from "../components/features/Modals";
import { TokenRefresher } from "../components/shared/TokenRefresher";
import { NewTokenRefresher } from "../components/shared/TokenRefresher/NewTokenRefresher";
import { isActiveToken } from "../utils/session/authorizationToken";
import { EJWTStatus, redirectSafe, validateJWT, isUUIDv4 } from "../utils";
import { isNewAuthorization, useAuthorizationStore } from "../store/authorizationStore";

/**
 * Main Layout of the application. Present as a root component for an application.
 */
export function MainLayout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const clearToken = usePersistStore(state => state.clearJwt);
  const clearDeviceToken = usePersistStore(state => state.clearDeviceJwt);
  const isLoginRoute = pathname === RoutesMap.login.index || pathname.startsWith(`${RoutesMap.login.index}/`);
  const canShowProtectedContent = useAuthorizationStore(state => state.version !== "keycloak" || isActiveToken(state.token));

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        if ((event.action.error as Error).name === "NetworkError") {
          const networkError = event.action.error as NetworkError;
          switch (networkError.internalCode) {
            case 10000: {
              // 500 Internal Server Error
              console.error("Server error", networkError);
              return;
            }
            case 1001: {
              // 401 Unauthorized
              if (isNewAuthorization()) {
                return;
              }

              const loginFormStore = useLoginFormStore.getState();
              clearToken();
              clearDeviceToken();
              loginFormStore.clearPhoneForm();
              loginFormStore.clearSMSForm();
              loginFormStore.clearForm();
              navigate(RoutesMap.login.index);
              return;
            }
            default: {
              console.error(networkError);
              return;
            }
          }
        }
      }
    });
  }, []);

  return (
    <AppModalProvider>
      {!isLoginRoute && (isNewAuthorization() ? <NewTokenRefresher /> : <TokenRefresher />)}
      <Container>
        {(isLoginRoute || canShowProtectedContent) && <Outlet />}
      </Container>
    </AppModalProvider>
  );
}

/**
 * JWT Session behavior.
 * * Invalid - should be redirected to the login page;
 * * RefreshRequired - token should be refreshed;
 * * Valid - do nothing;
 * * Expired - the token is expired but present and can be refreshed by API
 */
MainLayout.loader = ({ request }: LoaderFunctionArgs) => {
  if (isNewAuthorization()) {
    const auth = useAuthorizationStore.getState();
    if (isActiveToken(auth.token)) {
      return null;
    }

    const loginRoute = new URL(request.url).pathname.startsWith("/login");
    if (loginRoute) {
      return null;
    }

    return redirectSafe(auth.deviceId ? RoutesMap.login.pin : RoutesMap.login.index, request.url);
  }

  const state = usePersistStore.getState();
  const deviceJwtState = validateJWT(state.deviceJWT);

  if (!state.fingerprint || !isUUIDv4(state.fingerprint)) {
    let uniqueId: string;

    if (typeof crypto.randomUUID === "function") {
      uniqueId = crypto.randomUUID();
    } else {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
      uniqueId = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    state.setFingerprint(uniqueId);
  }

  switch (deviceJwtState) {
    case EJWTStatus.RefreshRequired:
    case EJWTStatus.Expired: {
      return redirectSafe(RoutesMap.login.pin, request.url); // TODO: @adil - keep in mind to change
    }
    case EJWTStatus.Invalid: {
      state.clearDeviceJwt();
      return redirectSafe(RoutesMap.login.index, request.url);
    }
    case EJWTStatus.Valid:
      return null;
  }
};
